// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { useEffect, useState, type ReactNode } from "react";
import { Lock, ChevronRight, CircleHelp } from "lucide-react";
import { NavLink } from "react-router-dom";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { NavItem } from "@/types/navigation";
import { useAppNavigation } from "@/hooks/useAppNavigation";
import { useTooltipsEnabled } from "@/hooks/useTooltipsEnabled";

interface SidebarNavItemProps {
  item: NavItem;
}

function SidebarNavInfoTooltip({ tooltip }: { tooltip: ReactNode }) {
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="ml-auto shrink-0 rounded-sm p-0.5 text-sidebar-foreground/50 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          aria-label="More information"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <CircleHelp className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-sm p-3 text-left text-sm">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

function SidebarComingSoonHover({
  tooltip,
  active,
  children,
}: {
  tooltip: ReactNode;
  active: boolean;
  children: ReactNode;
}) {
  const { state, isMobile } = useSidebar();
  if (!active || state === "collapsed" || isMobile) {
    return <>{children}</>;
  }

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" className="max-w-sm p-3 text-left text-sm">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export function SidebarNavItem({ item }: SidebarNavItemProps) {
  const { canAccess, isItemActive, shouldExpandGroup, navigateToItem, lockReason } =
    useAppNavigation();
  const { isMobile, setOpenMobile, state } = useSidebar();
  const tooltipsEnabled = useTooltipsEnabled();
  const hasAccess = canAccess(item);
  const anyChildAccess =
    item.children?.some((child) => canAccess(child)) ?? false;
  const canExpandGroup = hasAccess || anyChildAccess;
  const reason = lockReason(item);
  const isComingSoon = reason === "comingSoon";
  const isActive = isItemActive(item);
  const hasChildren = item.children && item.children.length > 0;
  const routeExpanded = shouldExpandGroup(item);
  const [open, setOpen] = useState(routeExpanded);
  const showInfoTooltip =
    tooltipsEnabled &&
    Boolean(item.tooltip) &&
    state !== "collapsed" &&
    hasAccess &&
    !isComingSoon;
  const sidebarTooltip =
    tooltipsEnabled && item.tooltip
      ? typeof item.tooltip === "string"
        ? item.tooltip
        : { children: item.tooltip }
      : item.label;
  const menuButtonTooltip =
    isComingSoon && state !== "collapsed" ? undefined : sidebarTooltip;

  useEffect(() => {
    if (routeExpanded) setOpen(true);
  }, [routeExpanded]);

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };

  if (!hasChildren) {
    const menuButton = (
      <SidebarMenuButton
        asChild={hasAccess}
        isActive={isActive}
        tooltip={menuButtonTooltip}
        className={cn(!hasAccess && "opacity-60", !hasAccess && isComingSoon && "cursor-default")}
        onClick={hasAccess ? undefined : () => navigateToItem(item)}
      >
        {hasAccess ? (
          <NavLink
            to={item.path}
            aria-current={isActive ? "page" : undefined}
            onClick={closeMobileSidebar}
          >
            <item.icon className="h-4 w-4" />
            <span>{item.label}</span>
            {showInfoTooltip && <SidebarNavInfoTooltip tooltip={item.tooltip!} />}
            {!hasAccess && <Lock className="ml-auto h-3.5 w-3.5" />}
          </NavLink>
        ) : (
          <>
            <item.icon className="h-4 w-4" />
            <span>{item.label}</span>
            <Lock className="ml-auto h-3.5 w-3.5" />
          </>
        )}
      </SidebarMenuButton>
    );

    return (
      <SidebarMenuItem>
        <SidebarComingSoonHover
          tooltip={item.tooltip}
          active={Boolean(isComingSoon && item.tooltip && tooltipsEnabled)}
        >
          {menuButton}
        </SidebarComingSoonHover>
      </SidebarMenuItem>
    );
  }

  const groupMenuButton = (
    <SidebarMenuButton
      asChild={hasAccess}
      isActive={isActive}
      tooltip={menuButtonTooltip}
      className={cn(
        !hasAccess && !anyChildAccess && "opacity-60",
        canExpandGroup && "pr-8",
        !hasAccess && isComingSoon && "cursor-default",
      )}
      onClick={hasAccess || anyChildAccess ? undefined : () => navigateToItem(item)}
    >
      {hasAccess ? (
        <NavLink
          to={item.path}
          aria-current={isActive ? "page" : undefined}
          onClick={closeMobileSidebar}
          className="flex min-w-0 flex-1 items-center gap-2"
        >
          <item.icon className="h-4 w-4" />
          <span className="truncate">{item.label}</span>
          {showInfoTooltip && <SidebarNavInfoTooltip tooltip={item.tooltip!} />}
        </NavLink>
      ) : (
        <>
          <item.icon className="h-4 w-4" />
          <span>{item.label}</span>
          {!anyChildAccess && <Lock className="ml-auto h-3.5 w-3.5" />}
        </>
      )}
    </SidebarMenuButton>
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <SidebarComingSoonHover
          tooltip={item.tooltip}
          active={Boolean(isComingSoon && item.tooltip && tooltipsEnabled)}
        >
          {groupMenuButton}
        </SidebarComingSoonHover>

        {canExpandGroup && (
          <CollapsibleTrigger asChild>
            <SidebarMenuAction
              aria-label={`${open ? "Collapse" : "Expand"} ${item.label} menu`}
              onClick={(event) => event.stopPropagation()}
            >
              <ChevronRight
                className={cn(
                  "transition-transform",
                  open && "rotate-90",
                )}
              />
            </SidebarMenuAction>
          </CollapsibleTrigger>
        )}

        {canExpandGroup && (
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.children!.map((child) => (
                <SidebarModuleSubItem key={child.id} item={child} />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}

function SidebarModuleSubItem({ item }: { item: NavItem }) {
  const { canAccess, isItemActive, navigateToItem } = useAppNavigation();
  const { isMobile, setOpenMobile } = useSidebar();
  const tooltipsEnabled = useTooltipsEnabled();
  const hasAccess = canAccess(item);
  const isActive = isItemActive(item);

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };

  const subButton = (
    <SidebarMenuSubButton
      asChild={hasAccess}
      isActive={isActive}
      onClick={hasAccess ? undefined : () => navigateToItem(item)}
    >
      {hasAccess ? (
        <NavLink
          to={item.path}
          aria-current={isActive ? "page" : undefined}
          onClick={closeMobileSidebar}
          className="flex min-w-0 flex-1 items-center gap-2"
        >
          <span className="truncate">{item.label}</span>
        </NavLink>
      ) : (
        <span className="flex items-center gap-2">
          {item.label}
          <Lock className="h-3 w-3" />
        </span>
      )}
    </SidebarMenuSubButton>
  );

  return (
    <SidebarMenuSubItem>
      {item.tooltip && hasAccess && tooltipsEnabled ? (
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>{subButton}</TooltipTrigger>
          <TooltipContent side="right" className="max-w-sm p-3 text-left">
            {item.tooltip}
          </TooltipContent>
        </Tooltip>
      ) : (
        subButton
      )}
    </SidebarMenuSubItem>
  );
}
